// AmbientModal.tsx — criar e editar um ambiente (o espaço físico da loja).
//
// Este modal é o mais chamado do repo e NUNCA existiu: `handleOpenModal
// ("ambient", ...)` aparece em 6 pontos de Ambients.tsx e 2 de Subsidiary.tsx,
// e até hoje clicar em qualquer um deles não abria nada — o `switch` de
// `renderModalContent` não tinha o case, então o clique simplesmente não fazia
// efeito. Criar ou editar ambiente era uma porta pintada na parede.
//
// TRÊS FORMATOS DE ENTRADA, e os três chegam aqui:
//   (a) o cartão da tela de Ambientes manda o ambiente INTEIRO -> edição;
//   (b) "Novo Ambiente" manda `{ subsidiary: <loja> }` -> criação com a loja
//       já escolhida e travada;
//   (c) o EmptyState chama `handleOpenModal("ambient")` sem argumento nenhum,
//       e o shell transforma isso em `{}` -> criação com a loja a escolher.
// A diferença entre (a) e (c) SÓ pode ser feita por `selectedObject
// ?.ambientUuid`: `{}` é truthy, então `if (selectedObject)` diria "edição"
// para o formulário vazio e o título sairia "Editar Ambiente" no cadastro.
//
// Subsidiary.tsx é o caso (b) com outra intenção: o botão de lá quer VER os
// ambientes daquela loja, não criar um. Como não há tela de detalhe, ele cai
// neste modal; então quando a loja já tem ambientes o formulário diz quantos
// são, em texto, e deixa claro que o que está aberto é um cadastro novo.
//
// Este modal NÃO faz requisição: ele monta o payload e chama
// `handleSave("ambient", form)`. Quem grava e quem fecha é o shell.
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import * as EstilosDoAmbiente from "../../../styles/components/configuration/modals/AmbientModal";
import {
  TIPOS_DE_AMBIENTE,
  TIPOS_DE_AMBIENTE_POR_NOME,
  APARENCIA_DE_TIPO_DESCONHECIDO,
} from "../sections/storesSection/tiposDeAmbiente";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface AmbientModalProps {
  /** `{}` quando é criação — sempre testar por `ambientUuid`, nunca por ele. */
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  decryptedInfoToken?: any;
}

interface FormularioDeAmbiente {
  ambientName: string;
  ambientDescription: string;
  ambientType: string;
  /** UUID da filial. String vazia = ainda não escolhida. */
  subsidiary: string;
  /** Texto porque input numérico vazio não é 0 — é "não informado". */
  capacity: string;
  ambientStatus: boolean;
}

const FORMULARIO_VAZIO: FormularioDeAmbiente = {
  ambientName: "",
  ambientDescription: "",
  ambientType: "",
  subsidiary: "",
  capacity: "",
  ambientStatus: true,
};

/**
 * A loja chega de dois jeitos: objeto inteiro (Ambients.tsx e Subsidiary.tsx
 * mandam a filial do token) ou só o uuid (é assim que vem dentro do próprio
 * ambiente, em `ambient.subsidiary`).
 */
function uuidDaLoja(valor: any): string {
  if (typeof valor === "string") return valor;
  return valor?.subsidiaryUuid ?? "";
}

const AmbientModal: React.FC<AmbientModalProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-ambientmodal",
  });

  const [form, setForm] = useState<FormularioDeAmbiente>(FORMULARIO_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  const ehEdicao = Boolean(selectedObject?.ambientUuid);

  /** Loja imposta de fora: caso (b). Em (a) e (c) isto é string vazia. */
  const lojaImposta = ehEdicao ? "" : uuidDaLoja(selectedObject?.subsidiary);

  /** As lojas reais do token — mesma travessia de Ambients.tsx. */
  const lojas: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) => empresa?.subsidiaries ?? []);
  }, [decryptedInfoToken]);

  const lojaEscolhida = useMemo(
    () => lojas.find((loja: any) => loja?.subsidiaryUuid === form.subsidiary),
    [lojas, form.subsidiary]
  );

  useEffect(() => {
    if (selectedObject?.ambientUuid) {
      setForm({
        ambientName: selectedObject.ambientName ?? "",
        ambientDescription: selectedObject.ambientDescription ?? "",
        ambientType: selectedObject.ambientType ?? "",
        subsidiary: uuidDaLoja(selectedObject.subsidiary),
        // `?? ""` e não `|| ""`: capacidade 0 é um número informado.
        capacity:
          selectedObject.capacity === null ||
          selectedObject.capacity === undefined
            ? ""
            : String(selectedObject.capacity),
        // Ambiente antigo sem a coluna preenchida abre como ativo, que é o
        // que a tela de Ambientes já mostra para `undefined`.
        ambientStatus: selectedObject.ambientStatus !== false,
      });
    } else {
      setForm({
        ...FORMULARIO_VAZIO,
        subsidiary: uuidDaLoja(selectedObject?.subsidiary),
      });
    }
    setAviso(null);
  }, [selectedObject]);

  const handleLocalChange = (
    campo: keyof FormularioDeAmbiente,
    valor: string | boolean
  ) => {
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
    // O aviso de validação some assim que o usuário mexe: aviso que sobrevive
    // à correção vira ruído e o usuário passa a ignorar todos.
    if (aviso?.tom === "erro") setAviso(null);
  };

  const handleSubmit = async () => {
    if (!form.ambientName.trim()) {
      setAviso({ tom: "erro", texto: "Dê um nome ao ambiente." });
      return;
    }
    if (!form.subsidiary) {
      setAviso({
        tom: "erro",
        texto:
          "Escolha a loja. Um ambiente é um espaço DENTRO de uma loja — sem " +
          "ela o servidor não tem onde pendurar o cadastro.",
      });
      return;
    }

    let capacidade: number | undefined;
    if (form.capacity.trim() !== "") {
      const numero = Number(form.capacity);
      if (!Number.isInteger(numero) || numero < 0) {
        setAviso({
          tom: "erro",
          texto:
            "Capacidade tem de ser um número inteiro de pessoas (ou ficar em " +
            "branco).",
        });
        return;
      }
      capacidade = numero;
    }

    // O payload sai com os nomes das COLUNAS, iguais aos do corpo de
    // `POST /ambient`. Campo opcional em branco vai como `undefined` e some do
    // JSON — mandar `""` gravaria string vazia por cima do que já havia.
    const payload = {
      ambientUuid: selectedObject?.ambientUuid,
      ambientName: form.ambientName.trim(),
      ambientDescription: form.ambientDescription.trim() || undefined,
      ambientType: form.ambientType || undefined,
      capacity: capacidade,
      subsidiary: form.subsidiary,
      ambientStatus: form.ambientStatus,
    };

    setSalvando(true);
    try {
      const resultado = await handleSave("ambient", payload);
      // `null` significa que o shell não tinha o que dizer. Só o `ok: false`
      // segura o modal aberto — fechar no erro é a mentira que este repo já
      // pagou caro.
      if (resultado && !resultado.ok) {
        setAviso({ tom: resultado.tom ?? "erro", texto: resultado.texto });
      }
    } catch (erro: any) {
      setAviso({
        tom: "erro",
        texto:
          erro?.response?.data?.message ??
          erro?.message ??
          "O salvamento falhou e o servidor não disse por quê. Nada foi gravado.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const aparenciaDoTipo = form.ambientType
    ? TIPOS_DE_AMBIENTE_POR_NOME[form.ambientType] ??
      APARENCIA_DE_TIPO_DESCONHECIDO
    : null;

  /**
   * Quantos ambientes a loja já tem, contados no token — dado real, não
   * inventado. Serve ao caso (b) vindo de Subsidiary.tsx, onde o clique queria
   * VER os ambientes e o que abriu foi um cadastro novo.
   */
  const ambientesJaCadastrados: number =
    lojaEscolhida?.ambients?.length ??
    (Array.isArray(selectedObject?.subsidiary?.ambients)
      ? selectedObject.subsidiary.ambients.length
      : 0);

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} medium>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-ambientmodal">
            <AlfredoStyles.MaterialIcons>room</AlfredoStyles.MaterialIcons>
            {ehEdicao ? "Editar Ambiente" : "Novo Ambiente"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Identificação
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label htmlFor="ambientName">
                    Nome do ambiente *
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    id="ambientName"
                    type="text"
                    value={form.ambientName}
                    onChange={(e) =>
                      handleLocalChange("ambientName", e.target.value)
                    }
                    placeholder="Salão, Cozinha, Depósito…"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label htmlFor="ambientDescription">
                    Descrição
                  </AlfredoStyles.Label>
                  <AlfredoStyles.TextArea
                    id="ambientDescription"
                    value={form.ambientDescription}
                    onChange={(e) =>
                      handleLocalChange("ambientDescription", e.target.value)
                    }
                    placeholder="Para que serve este espaço"
                  />
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Onde fica e para que serve
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label htmlFor="subsidiary">
                    Loja *
                  </AlfredoStyles.Label>
                  {lojaImposta ? (
                    // Caso (b): quem abriu o modal já disse de qual loja
                    // partiu. Trocar a loja aqui seria criar o ambiente em
                    // outro lugar sem o usuário perceber.
                    <EstilosDoAmbiente.LojaTravada>
                      <AlfredoStyles.MaterialIcons
                        style={{ fontSize: "var(--fs-md)" }}
                      >
                        lock
                      </AlfredoStyles.MaterialIcons>
                      {lojaEscolhida?.subsidiaryName ??
                        selectedObject?.subsidiary?.subsidiaryName ??
                        "Loja selecionada"}
                    </EstilosDoAmbiente.LojaTravada>
                  ) : (
                    <AlfredoStyles.Select
                      id="subsidiary"
                      value={form.subsidiary}
                      onChange={(e) =>
                        handleLocalChange("subsidiary", e.target.value)
                      }
                    >
                      <option value="">Selecione a loja</option>
                      {lojas.map((loja: any) => (
                        <option
                          key={loja.subsidiaryUuid}
                          value={loja.subsidiaryUuid}
                        >
                          {loja.subsidiaryName}
                        </option>
                      ))}
                    </AlfredoStyles.Select>
                  )}
                  {!lojaImposta && lojas.length === 0 && (
                    <EstilosDoAmbiente.TextoDeAjuda>
                      Nenhuma loja no seu acesso. Cadastre a loja primeiro — o
                      ambiente é um espaço dentro dela.
                    </EstilosDoAmbiente.TextoDeAjuda>
                  )}
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label htmlFor="ambientType">
                    Tipo
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    id="ambientType"
                    value={form.ambientType}
                    onChange={(e) =>
                      handleLocalChange("ambientType", e.target.value)
                    }
                  >
                    <option value="">Sem tipo definido</option>
                    {TIPOS_DE_AMBIENTE.map((tipo) => (
                      <option key={tipo.valor} value={tipo.valor}>
                        {tipo.valor}
                      </option>
                    ))}
                    {/*
                      Tipo gravado que saiu da lista continua aparecendo, senão
                      abrir e salvar o ambiente apagaria o valor em silêncio.
                      O backend recusa com 400 um tipo fora dos cinco, então
                      salvar sem trocá-lo devolve o motivo no aviso.
                    */}
                    {form.ambientType &&
                      !TIPOS_DE_AMBIENTE_POR_NOME[form.ambientType] && (
                        <option value={form.ambientType}>
                          {form.ambientType} (tipo antigo)
                        </option>
                      )}
                  </AlfredoStyles.Select>
                  {aparenciaDoTipo && (
                    <EstilosDoAmbiente.SeloDoTipo $cor={aparenciaDoTipo.color}>
                      <AlfredoStyles.MaterialIcons
                        style={{ fontSize: "var(--fs-xs)" }}
                      >
                        {aparenciaDoTipo.icon}
                      </AlfredoStyles.MaterialIcons>
                      {form.ambientType}
                    </EstilosDoAmbiente.SeloDoTipo>
                  )}
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label htmlFor="capacity">
                    Capacidade (pessoas)
                  </AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    id="capacity"
                    type="number"
                    min={0}
                    step={1}
                    value={form.capacity}
                    onChange={(e) =>
                      handleLocalChange("capacity", e.target.value)
                    }
                    placeholder="Opcional"
                  />
                  <EstilosDoAmbiente.TextoDeAjuda>
                    Em branco significa "não informado" — o cartão do ambiente
                    esconde o selo em vez de mostrar "0 pessoas".
                  </EstilosDoAmbiente.TextoDeAjuda>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Situação</AlfredoStyles.Label>
                  <AlfredoStyles.CheckboxContainer>
                    <AlfredoStyles.CheckboxInput>
                      <input
                        type="checkbox"
                        checked={form.ambientStatus}
                        onChange={(e) =>
                          handleLocalChange("ambientStatus", e.target.checked)
                        }
                      />
                      <span className="checkmark" />
                    </AlfredoStyles.CheckboxInput>
                    <AlfredoStyles.CheckboxLabel active={form.ambientStatus}>
                      {form.ambientStatus ? "Ativo" : "Inativo"}
                    </AlfredoStyles.CheckboxLabel>
                  </AlfredoStyles.CheckboxContainer>
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            {/*
              NÃO existe aviso permanente sobre `ambientType`/`capacity` aqui.
              As duas colunas JÁ existem em `acd."ActAmbient"`: são criadas no
              boot do Central por `garantirEstruturaDeSuprimentos`
              (ALTER TABLE ... ADD COLUMN IF NOT EXISTS, idempotente), e o
              `ambient.handler.ts` as grava por SQL cru porque o schema.prisma
              não as declara. Um banner dizendo "pode ser recusado" seria um
              medo falso e permanente na tela — quando houver recusa de verdade,
              ela chega do servidor e aparece no `aviso` aqui embaixo.
            */}

            {/*
              Subsidiary.tsx chama este modal querendo VER os ambientes da
              loja; o que existe é cadastro. Em vez de listar (a lista está na
              aba Ambientes, com o dado de verdade), o modal conta quantos são
              e diz o que ele é.
            */}
            {!ehEdicao && lojaImposta && ambientesJaCadastrados > 0 && (
              <AlfredoStyles.AlertMessage $tom="atencao">
                Esta loja já tem {ambientesJaCadastrados} ambiente
                {ambientesJaCadastrados !== 1 ? "s" : ""} cadastrado
                {ambientesJaCadastrados !== 1 ? "s" : ""}. O que está aberto
                aqui é o cadastro de um ambiente NOVO — para ver e editar os que
                já existem, use a aba Ambientes.
              </AlfredoStyles.AlertMessage>
            )}

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando…"
                : ehEdicao
                ? "Salvar alterações"
                : "Criar ambiente"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default AmbientModal;
